import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { formControlValue$ } from 'src/helpers/formControlValue$';

@Component({
  selector: 'app-text-search',
  templateUrl: './text-search.component.html',
  styleUrls: ['./text-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextSearchComponent implements OnInit {
  @Output() searchTextChanged = new EventEmitter<string>();

  private searchTextInput$ = new BehaviorSubject<string>('');
  searchText$ = this.searchTextInput$.asObservable();

  @Input() set searchText(text: string) {
    this.searchTextInput$.next(text || '');
    this.searchControl.setValue(text || '', { emitEvent: false });
  }
  get searchText() {
    return this.searchTextInput$.value;
  }

  @Input() useQueryParam: string | false = false;

  searchControl = new FormControl('');
  searchValue$ = formControlValue$(this.searchControl);

  // Effect for emitting search changes with debounce
  private searchEffect$ = this.searchValue$.pipe(
    debounceTime(300),
    distinctUntilChanged(),
    tap((searchValue) => {
      // value mismatch, can happen on initial load, skip
      if (searchValue !== this.searchControl.value) {
        return;
      }
      this.searchTextChanged.emit(searchValue);
      if (this.useQueryParam && searchValue !== undefined) {
        this.updateQueryParams({ [this.useQueryParam]: searchValue });
      }
    }),
  );

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
  ) {
    // Subscribe to search effect
    this.searchEffect$.subscribe();
  }

  ngOnInit(): void {
    this.getInitialQueryParams();
  }

  getInitialQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    if (this.useQueryParam && queryParams[this.useQueryParam]) {
      const initialValue = queryParams[this.useQueryParam];
      this.searchTextInput$.next(initialValue);
      this.searchControl.setValue(initialValue, { emitEvent: false });
      // Emit initial value
      this.searchTextChanged.emit(initialValue);
    }
  }

  search() {
    const currentValue = this.searchControl.value || '';
    this.searchTextInput$.next(currentValue);
    this.searchTextChanged.emit(currentValue);
    if (this.useQueryParam) {
      this.updateQueryParams({ [this.useQueryParam]: currentValue });
    }
  }

  private updateQueryParams(queryParams: { [key: string]: string }) {
    this.ngZone.run(() => {
      this.router
        .navigate([], {
          relativeTo: this.route,
          queryParams: queryParams,
          queryParamsHandling: 'merge',
        })
        .then();
    });
  }
}
